import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { observer } from 'mobx-react';
import { useGuide } from '@knocklabs/react';
import { v4 as uuidv4 } from 'uuid';

import { IconButton } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import { Button } from 'components/Button';
import { StyledTextField } from 'components/Input/styles';

import API from 'utils/api';
import { useStore } from 'utils/hooks/store';
import Logger from 'utils/logger';

import { rememberClosed, wasClosedRecently } from './closedState';
import {
  SurveyCard,
  Header,
  Title,
  ScoreRow,
  ScoreButton,
  ScoreLabels,
  CommentLabel,
  Actions,
  Confirmation,
} from './styles';

// Must match the backend NPS_GUIDE_KEY constance value so both sides target the same guide.
export const NPS_GUIDE_KEY = 'nps-survey';

const SCORES = Array.from({ length: 11 }, (_, index) => index);

const DEFAULT_QUESTION = 'How likely are you to recommend us to a colleague?';
const DEFAULT_COMMENT_QUESTION = 'What is the main reason for your score?';
const COMMENT_FIELD_ID = 'nps-survey-comment';
const MAX_COMMENT_LENGTH = 1000;

const SUBMIT_ERROR = 'Sorry, we could not send your feedback. Please try again.';
const CONFIRMATION_MESSAGE = 'Thanks, your feedback has been sent.';
const CONFIRMATION_AUTO_HIDE_MS = 5000;

type NpsSurveyContent = {
  question?: string;
  comment_question?: string;
  comment_question_detractor?: string;
  comment_question_passive?: string;
  comment_question_promoter?: string;
};

const bandCommentQuestion = (content: NpsSurveyContent, score: number | null): string | undefined => {
  if (score === null) {
    return undefined;
  }
  if (score >= 9) {
    return content.comment_question_promoter;
  }
  if (score >= 7) {
    return content.comment_question_passive;
  }
  return content.comment_question_detractor;
};

const commentQuestion = (content: NpsSurveyContent, score: number | null): string =>
  bandCommentQuestion(content, score)?.trim() || content.comment_question?.trim() || DEFAULT_COMMENT_QUESTION;

const NPSSurvey: React.FC = observer(() => {
  const { step } = useGuide<NpsSurveyContent>({ key: NPS_GUIDE_KEY });
  const { notification, knockGuides } = useStore();
  const userId = knockGuides.config?.userId ?? '';
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const seenRef = useRef(false);
  const responseUuidRef = useRef('');
  const submitAbortRef = useRef<AbortController | null>(null);
  const confirmationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (step && !seenRef.current) {
      step.markAsSeen();
      seenRef.current = true;
      responseUuidRef.current = uuidv4();
    } else if (!step) {
      seenRef.current = false;
    }
  }, [step]);

  useEffect(() => () => submitAbortRef.current?.abort(), []);

  useEffect(
    () => () => {
      if (confirmationTimeoutRef.current) {
        clearTimeout(confirmationTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (wasClosedRecently(userId)) {
      setIsClosed(true);
    }
  }, [userId]);

  if (!step || isClosed) {
    return null;
  }

  const close = (): void => {
    rememberClosed(userId);
    setIsClosed(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (score === null || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const trimmedComment = comment.trim();
    submitAbortRef.current = new AbortController();
    try {
      await API.user.submitNpsResponse(
        {
          uuid: responseUuidRef.current,
          action: 'submit',
          score,
          ...(trimmedComment ? { comment: trimmedComment } : {}),
        },
        submitAbortRef.current.signal
      )();
      setIsConfirmed(true);
      confirmationTimeoutRef.current = setTimeout(close, CONFIRMATION_AUTO_HIDE_MS);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      Logger.error('NPS survey submission failed', error);
      notification.enqueueErrorSnackbar(SUBMIT_ERROR);
      return;
    } finally {
      setIsSubmitting(false);
    }
    step.markAsInteracted({ metadata: { nps_score: score } }).catch((error) => {
      Logger.error('NPS survey interaction could not be recorded in Knock', error);
    });
  };

  const handleDismiss = (dismissType: 'hard' | 'soft'): void => {
    if (isSubmitting) {
      return;
    }
    close();
    API.user
      .submitNpsResponse({ uuid: responseUuidRef.current, action: 'dismiss', dismissType })()
      .catch((error) => {
        Logger.error('NPS survey dismissal failed', error);
      });
  };

  return (
    <SurveyCard role="dialog" aria-modal="false" aria-label="Net Promoter Score survey">
      <Header>
        <Title>{step.content?.question ?? DEFAULT_QUESTION}</Title>
        <IconButton
          aria-label="Close survey"
          size="small"
          disabled={isSubmitting && !isConfirmed}
          onClick={() => (isConfirmed ? close() : handleDismiss('hard'))}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Header>
      <ScoreRow>
        {SCORES.map((value) => (
          <ScoreButton
            key={value}
            type="button"
            $selected={score === value}
            aria-pressed={score === value}
            aria-label={`Score ${value}`}
            disabled={isConfirmed}
            onClick={() => setScore(value)}
          >
            {value}
          </ScoreButton>
        ))}
      </ScoreRow>
      <ScoreLabels>
        <span>Not likely</span>
        <span>Very likely</span>
      </ScoreLabels>
      {isConfirmed ? (
        <Confirmation>{CONFIRMATION_MESSAGE}</Confirmation>
      ) : (
        <>
          <CommentLabel htmlFor={COMMENT_FIELD_ID}>{commentQuestion(step.content ?? {}, score)}</CommentLabel>
          <StyledTextField
            id={COMMENT_FIELD_ID}
            placeholder="Your feedback..."
            multiline
            minRows={2}
            fullWidth
            slotProps={{ htmlInput: { maxLength: MAX_COMMENT_LENGTH } }}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <Actions>
            <Button variant="text" color="primary" disabled={isSubmitting} onClick={() => handleDismiss('soft')}>
              Ask me later
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={score === null || isSubmitting}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Actions>
        </>
      )}
    </SurveyCard>
  );
});

export default NPSSurvey;
